




























































































































































import { Component, Watch } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import Alert from '@/components/Shared/Alert.vue';
import PayrollRunStatus, { PayrollRunStatusLabels, PayrollRunStatusColors } from '@/models/Payroll/PayrollRunStatus';
import PayrollRunDto from '@/models/Payroll/PayrollRunDto';
import TimesheetDto from '@/models/Timesheets/TimesheetDto';
import {
    PayrollRunDetailsModule,
    PayrollRunWorkerSummary,
} from '@/store/modules/payrollRunDetails';
import PayrollRunTimesheets from '@/components/Payroll/PayrollRunTimesheets.vue';
import PayrollRunStatusEdit from '@/components/Payroll/PayrollRunStatusEdit.vue';
import PayrollRunWorkers from '@/components/Payroll/PayrollRunWorkers.vue';
import PayrollRunLocations from '@/components/Payroll/PayrollRunLocations.vue';
import PayrollRunPurePro from '@/components/Payroll/PayrollRunPurePro.vue';

interface StepAction {
    label: string;
    enabled: boolean;
    handler: () => Promise<void>;
}

@Component({
    name: 'AgencyPayrollRun',
    components: { Alert, PayrollRunTimesheets, PayrollRunWorkers, PayrollRunLocations, PayrollRunPurePro, PayrollRunStatusEdit },
})
export default class extends GiganautVue {
    activeTab = 0;

    get loading(): boolean { return PayrollRunDetailsModule.loading; }
    get saving(): boolean { return PayrollRunDetailsModule.saving; }
    get run(): PayrollRunDto | null { return PayrollRunDetailsModule.run; }
    get timesheets(): TimesheetDto[] { return PayrollRunDetailsModule.timesheets; }
    get workers(): PayrollRunWorkerSummary[] { return PayrollRunDetailsModule.workers; }

    // Name of the configured payroll integration (e.g. "PurePro"), surfaced
    // by the run GET handler. Passed into Timesheets + Workers components so
    // they can label Send buttons by provider.
    get providerName(): string {
        return this.run ? (this.run.payrollIntegrationName || '') : '';
    }

    // The PUREPRO tab is a provider-specific live view — only offered when
    // the agency's payroll integration is actually PurePro.
    get isPurePro(): boolean {
        return this.providerName === 'PurePro';
    }

    get statusLabel(): string { return this.run ? PayrollRunStatusLabels[this.run.status] || String(this.run.status) : ''; }
    get statusColor(): string { return this.run ? PayrollRunStatusColors[this.run.status] || 'grey' : 'grey'; }

    get formatPeriod(): string {
        if (!this.run) return '';
        return `${this.formatShortDate(this.run.periodStart)} – ${this.formatShortDate(this.run.periodEnd)}`;
    }

    get formatPayDay(): string {
        if (!this.run || !this.run.payDay) return '—';
        return this.formatShortDate(this.run.payDay);
    }

    get stepHelperText(): string {
        if (!this.run) return '';
        switch (this.run.status) {
            case PayrollRunStatus.Draft:
                return `Add timesheets on the Timesheets tab, then push ${this.Labels.workers.toLowerCase()} (${this.Labels.workers} tab) and timesheets (Timesheets tab) using the Send buttons.`;
            case PayrollRunStatus.Submitting:
                return `Send the ${this.Labels.workers.toLowerCase()} and timesheets that are still outstanding from their tabs. The run advances to Submitted automatically once everything is sent (or set the status manually via Set Status — allowed only when nothing is left unsent).`;
            case PayrollRunStatus.Submitted:
                return 'Everything is submitted. Run payroll in the payroll system, then use Set Status → Syncing Data to unlock the fetch steps — allowed once the "Send Payslips" control job has completed (see the PurePro tab). Reopening for late timesheets is blocked once "Finalise Payroll" has started.';
            case PayrollRunStatus.SyncingData:
                return `Run "Fetch payslips" and "Fetch holiday" when the data is available in the payroll system — each runs in the background and can be re-run safely (only outstanding ${this.Labels.workers.toLowerCase()} are re-pulled). The run completes automatically once every ${this.Labels.worker.toLowerCase()} has both synced.`;
            case PayrollRunStatus.Completed:
                return 'Run complete. Payslips and holiday balances are available.';
            case PayrollRunStatus.Failed:
                return 'Run failed. Retry the pull or contact support if the problem persists.';
            default:
                return '';
        }
    }

    // The Send buttons live on the Workers + Timesheets tabs and status
    // changes on the Set Status menu (top right). The Next Action panel
    // only carries the fetch steps, which exist solely in Syncing Data.
    get stepPrimaryAction(): StepAction | null {
        if (!this.run) return null;
        const id = this.run.id;
        switch (this.run.status) {
            case PayrollRunStatus.SyncingData:
                return { label: 'Fetch payslips', enabled: true, handler: () => PayrollRunDetailsModule.fetchPayslipsStep(id) };
            case PayrollRunStatus.Failed:
                return { label: 'Retry pull', enabled: true, handler: () => PayrollRunDetailsModule.retryPull(id) };
            default:
                return null;
        }
    }

    get stepSecondaryAction(): StepAction | null {
        if (!this.run) return null;
        const id = this.run.id;
        if (this.run.status === PayrollRunStatus.SyncingData) {
            return { label: 'Fetch holiday', enabled: true, handler: () => PayrollRunDetailsModule.fetchHolidayStep(id) };
        }
        return null;
    }

    async onStepAction() {
        if (this.stepPrimaryAction && this.stepPrimaryAction.handler) {
            await this.stepPrimaryAction.handler();
        }
    }

    async onStepSecondaryAction() {
        if (this.stepSecondaryAction && this.stepSecondaryAction.handler) {
            await this.stepSecondaryAction.handler();
        }
    }

    formatShortDate(s: string | null): string {
        if (!s) return '';
        const d = new Date(s);
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }

    formatDate(s: string | null): string {
        if (!s) return '';
        const d = new Date(s);
        return isNaN(d.getTime()) ? '' : d.toLocaleString();
    }

    formatPence(p: number): string {
        if (!p) return '£0.00';
        return '£' + (p / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    async beforeMount() {
        // Only fetch the run itself up front (the DETAILS tab needs it).
        // Timesheets / workers / errors load lazily — see onActiveTabChanged.
        PayrollRunDetailsModule.cleanState();
        const id = this.$route.params.payrollRunId;
        await PayrollRunDetailsModule.fetch(id);
    }

    // Lazy tab loaders. Each watcher fire only fetches the active tab's data
    // (and only if it hasn't been loaded since the last invalidation).
    // The TIMESHEETS / WORKERS / LOCATIONS panels are extracted components
    // that ensure-load on mount, but v-tab-item keeps them alive after the
    // first render — so re-activating a tab whose data was invalidated by a
    // mutation elsewhere (e.g. add/remove timesheets changes the worker and
    // location sets) would show stale data without this watcher re-running
    // the ensure call. Ensure-loaded is a no-op while the flag is still set,
    // so switching tabs never refetches unnecessarily.
    @Watch('activeTab')
    async onActiveTabChanged(tabIdx: number) {
        if (!this.run) return;
        if (tabIdx === 1) {
            await Promise.all([
                PayrollRunDetailsModule.ensureTimesheetsLoaded(this.run.id),
                PayrollRunDetailsModule.ensureAvailableTimesheetsLoaded(this.run.id),
            ]);
        } else if (tabIdx === 2) {
            await PayrollRunDetailsModule.ensureWorkersLoaded(this.run.id);
        } else if (tabIdx === 3) {
            await PayrollRunDetailsModule.ensureLocationsLoaded(this.run.id);
        }
        // Tab 4 (PUREPRO, when shown) manages its own sub-tab loading —
        // including the run errors — inside PayrollRunPurePro.
    }
}
